import { useProductList } from "@/api/services/admin/product";
import { useProductContractsByOrganization } from "@/api/services/admin/product-contract";
import { TProductListItem } from "@/api/services/admin/product/dtos/get-product-list.dto";
import { TUserProductContractItem } from "@/api/services/admin/user/dtos/get-user-detail.dto";
import { Checkbox } from "@/components/elements/Checkbox";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { SectionCard } from "@/components/elements/SectionCard";
import { Table, TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { useSortedProducts } from "@/features/organization/hooks/useSortedProducts";
import { useTable } from "@/hooks/useTable";
import { black, state, text } from "@/theme/colors";
import { EContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { useParams } from "react-router-dom";

type Props = {
  userProductContracts: TUserProductContractItem[];
  selectedContractIds: number[];
  onSelectedContractIdsChange: (contractIds: number[]) => void;
  organizationId?: string;
  includeContractStatuses?: EContractStatus[];
};

export const UserProductSelect: FC<Props> = ({
  userProductContracts,
  selectedContractIds,
  onSelectedContractIdsChange,
  organizationId,
  includeContractStatuses,
}) => {
  const params = useParams();
  const orgId = organizationId ?? params.id;

  const { data: productData, isLoading: isProductLoading } = useProductList();
  const {
    data: contractData,
    isLoading: isContractLoading,
    isFetching: isContractFetching,
  } = useProductContractsByOrganization(orgId, { includeContractStatuses });

  const productContracts = contractData?.data;

  const contractMap = useMemo(() => new Map(productContracts?.map((contract) => [contract.product?.code, contract]) ?? []), [productContracts]);
  const userContractMap = useMemo(() => new Map(userProductContracts.map((contract) => [contract.productCode, contract]) ?? []), [productContracts]);

  const products = useMemo(() => {
    const items = productData?.data ?? [];
    return items
      .filter((item) => contractMap.has(item.code))
      .map((item) => {
        const contract = contractMap.get(item.code);
        const userContract = userContractMap.get(item.code);

        if (!contract || !userContract) return item;
        return {
          ...item,
          productContract: {
            id: contract.id,
            isTemporary: contract.isTemporary,
            plan: contract.plan,
            referenceProductId: contract.referenceProductId,
            lastLoginAt: userContract?.lastLoginAt,
            status: contract.status || null,
            activeUserCount: contract.activeUserCount,
            activatedAt: userContract?.activatedAt,
            createdAt: userContract.createdAt,
          },
        };
      });
  }, [productData, contractMap]);

  const sortedProducts = useSortedProducts(products);

  // Contract ids the org actually holds — the pool a user can be granted access to.
  const selectableContractIds = useMemo(
    () => sortedProducts.map((item) => contractMap.get(item.code)?.id).filter((id): id is number => id != null),
    [sortedProducts, contractMap],
  );
  const hasSelectable = selectableContractIds.length > 0;
  const isAllSelected = hasSelectable && selectableContractIds.every((id) => selectedContractIds.includes(id));

  const toggleContract = (contractId: number) =>
    onSelectedContractIdsChange(
      selectedContractIds.includes(contractId) ? selectedContractIds.filter((id) => id !== contractId) : [...selectedContractIds, contractId],
    );

  const toggleAll = () =>
    onSelectedContractIdsChange(
      isAllSelected
        ? selectedContractIds.filter((id) => !selectableContractIds.includes(id))
        : Array.from(new Set([...selectedContractIds, ...selectableContractIds])),
    );

  const columns: MRT_ColumnDef<TProductListItem>[] = useMemo(
    () => [
      {
        id: "select",
        header: "",
        size: 50,
        Header: () => {
          if (!hasSelectable) return null;
          return <Checkbox checked={isAllSelected} onChange={toggleAll} sx={{ p: 0 }} />;
        },
        // Checked purely reflects whether the user's product contract matches this org contract.
        Cell: ({ row }) => {
          const contractId = contractMap.get(row.original.code)?.id;
          const selectable = contractId != null;
          const checked = selectable && selectedContractIds.includes(contractId);
          return <Checkbox checked={checked} disabled={!selectable} onChange={() => selectable && toggleContract(contractId)} sx={{ p: 0 }} />;
        },
      },
      {
        accessorKey: "name",
        header: "プロダクト名",
        size: 176,
        Header: () => (
          <TableSorting name="productName">
            <Typography variant="body14Bold" color={text.primary}>
              プロダクト名
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const isActiveContract =
            !row.original.productContract ||
            row.original.productContract.status === null ||
            row.original.productContract.status === EContractStatus.DURING_CONTRACT ||
            row.original.productContract.status === EContractStatus.TRIAL;
          return (
            <EllipsisText
              title={row.original.name ?? ""}
              variant="body14"
              maxWidth={150}
              sx={{ textDecoration: isActiveContract ? undefined : "line-through" }}
            />
          );
        },
      },
      {
        id: "activeUserCount",
        header: "有効ユーザー数",
        size: 130,
        Cell: ({ row }) => row.original.productContract?.activeUserCount || EMPTY_STR.DATE,
      },
      {
        id: "lastLoginAt",
        header: "最終ログイン",
        size: 148,
        Header: () => (
          <TableSorting name="lastLoginAt">
            <Typography variant="body14Bold" color={text.primary}>
              最終ログイン
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) =>
          row.original.productContract?.lastLoginAt ? dayjs(row.original.productContract.lastLoginAt).format(DATE_FORMAT) : EMPTY_STR.DATE,
      },

      {
        id: "createdAt",
        header: "ご利用開始日",
        size: 108,
        Header: () => (
          <TableSorting name="createdAt">
            <Typography variant="body14Bold" color={text.primary}>
              ご利用開始日
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) =>
          row.original.productContract?.createdAt ? dayjs(row.original.productContract.createdAt).format(DATE_FORMAT) : EMPTY_STR.DATE,
      },
    ],
    [contractMap, selectedContractIds, selectableContractIds, isAllSelected, hasSelectable, onSelectedContractIdsChange],
  );

  const { table } = useTable({
    columns,
    rows: sortedProducts,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original.productContract?.isTemporary ? `${state.warning_3} !important` : undefined,
      },
    }),
  });

  return (
    <SectionCard title="プロダクトアクセス権限" titleVariant="head20Bold" maxWidth="100%" titleColor={black[30]}>
      <Stack gap={2}>
        <Table table={table} isLoading={isProductLoading || isContractLoading} isFetching={isContractFetching} />
        <Typography variant="body14" color={text.secondary}>
          ユーザーがアクセス可能なプロダクトをチェックしてください。組織が契約していないプロダクトは付与できません。
        </Typography>
      </Stack>
    </SectionCard>
  );
};
